import { useEffect, useState, useRef } from 'react'
import Styles from './Screenshot.module.scss'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { useCreateScreenshot } from 'hooks/queries/useCreateScreenshot'

const SCREENSHOT_TIMEOUT = 30000

interface Props {
  url?: string
  handleBack: () => void
  cancelOnError?: boolean
}

export const Screenshot = ({ url, handleBack, cancelOnError = true }: Props) => {
  const { mutateAsync: createScreenshot, isLoading: isMutating } = useCreateScreenshot()
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (screenshotUrl || isMutating) {
      return
    }

    const fetchScreenshot = async () => {
      if (url) {
        timeoutRef.current = setTimeout(() => {
          if (cancelOnError) handleBack()
        }, SCREENSHOT_TIMEOUT)

        try {
          const result = await createScreenshot({
            url,
          })

          if (result) {
            setScreenshotUrl(result)
          }
        } catch (error: unknown) {
          if (cancelOnError) handleBack()
        } finally {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
          }
        }
      }
    }

    fetchScreenshot()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [createScreenshot, screenshotUrl, isMutating, url, handleBack, cancelOnError])

  return (
    <div className={Styles.imageContainer}>
      {screenshotUrl ? (
        <img src={screenshotUrl} alt="Initial Screenshot" />
      ) : (
        <LoadingShimmerer>
          <div className={Styles.screenshotPlaceholder} />
        </LoadingShimmerer>
      )}
    </div>
  )
}
